import "./DistanceMeasurer.css";
import Overlay from "ol/Overlay";
import { vector } from "../../_config/layers/";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { LineString } from "ol/geom";
import { getLength } from "ol/sphere";
import { unByKey } from "ol/Observable";
import Draw from "ol/interaction/Draw";
import React, { useState, useEffect, useRef } from "react";
import { source } from "../../_config/layers/vector";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

let draw, sketch;
let helpTooltipElement, helpTooltip;
let measureTooltipElement, measureTooltip;

function DistanceMeasurer(props) {
  const { t } = useTranslation();
  const htmlElement = useRef();
  const [active, setActive] = useState(false);

  const formatLength = (line) => {
    const length = getLength(line, { projection: "EPSG:4326" });
    return length > 100
      ? `${Math.round((length / 1000) * 100) / 100} ${t("distanceMeasurer.km")}`
      : `${Math.round(length * 100) / 100} ${t("distanceMeasurer.m")}`;
  };

  const pointerMoveHandler = (evt) => {
    if (evt.dragging) return;
    helpTooltipElement.innerHTML = sketch
      ? t("distanceMeasurer.continueMsg")
      : t("distanceMeasurer.initMsg");
    helpTooltip.setPosition(evt.coordinate);
    helpTooltipElement.classList.remove("hidden");
  };

  const addInteraction = () => {
    draw = new Draw({
      source,
      type: "LineString",
      style: new Style({
        fill: new Fill({ color: "rgba(255,255,255,0.2)" }),
        stroke: new Stroke({
          color: "rgba(0,0,0,0.5)",
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({ color: "rgba(0,0,0,0.7)" }),
          fill: new Fill({ color: "rgba(255,255,255,0.2)" }),
        }),
      }),
    });

    props.map.addInteraction(draw);
    createHelpTooltip();
    createMeasureTooltip();

    let listener;

    draw.on("drawstart", (evt) => {
      sketch = evt.feature;
      let tooltipCoord = evt.coordinate;

      listener = sketch.getGeometry().on("change", (evt) => {
        const geom = evt.target;
        if (geom instanceof LineString) {
          measureTooltipElement.innerHTML = formatLength(geom);
          tooltipCoord = geom.getLastCoordinate();
          measureTooltip.setPosition(tooltipCoord);
        }
      });
    });

    draw.on("drawend", () => {
      measureTooltipElement.className = "ol-tooltip ol-tooltip-static";
      measureTooltip.setOffset([0, -7]);
      sketch = null;
      measureTooltipElement = null;
      createMeasureTooltip();
      unByKey(listener);
    });
  };

  const createHelpTooltip = () => {
    if (helpTooltipElement) helpTooltipElement.remove();
    helpTooltipElement = document.createElement("div");
    helpTooltipElement.className = "ol-tooltip hidden";
    helpTooltip = new Overlay({
      element: helpTooltipElement,
      offset: [15, 0],
      positioning: "center-left",
    });
    props.map.addOverlay(helpTooltip);
  };

  const createMeasureTooltip = () => {
    if (measureTooltipElement) measureTooltipElement.remove();
    measureTooltipElement = document.createElement("div");
    measureTooltipElement.className = "ol-tooltip ol-tooltip-measure";
    measureTooltip = new Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: "bottom-center",
      stopEvent: false,
      insertFirst: false,
    });
    props.map.addOverlay(measureTooltip);
  };

  const handleActivity = (activate) => {
    if (activate) {
      props.map.on("pointermove", pointerMoveHandler);
      props.map.getViewport().addEventListener("mouseout", () => {
        helpTooltipElement.classList.add("hidden");
      });
      addInteraction();
      setActive(true);
    } else {
      props.map.removeInteraction(draw);
      if (helpTooltipElement) helpTooltipElement.style.display = "none";
      setActive(false);
      source.clear();
      document
        .querySelectorAll(".ol-tooltip-static")
        .forEach((el) => el.remove());
    }
  };

  useEffect(() => handleActivity(active), [active]);

  useEffect(() => {
    if (props.activeTool !== htmlElement) handleActivity(false);
  }, [props.activeTool]);

  const handleClick = () => {
    vector.getSource().clear();
    setActive(!active);
    props.handleActiveTool(htmlElement);
  };

  return (
    <>
      <Button
        alt={t("distanceMeasurer.buttonTooltip")}
        title={t("distanceMeasurer.buttonTooltip")}
        onClick={handleClick}
        ref={htmlElement}
        className={`DistanceMeasurerButton DistanceMeasurer ${
          active ? "DistanceMeasurerActive" : "DistanceMeasurerDeactive"
        }`}
        severity="info"
        rounded
        icon="pi pi-arrows-h"
      />

      <Tooltip
        target=".DistanceMeasurerButton"
        content={t("distanceMeasurer.buttonTooltip")}
        position="bottom"
        className="CustomTooltip"
      />
    </>
  );
}

export default DistanceMeasurer;
